import qs from "qs";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import FilterOption from "../../../components/FilterOption/FilterOption";
import SSRSuspense from "../../../components/SSRSuspense/SSRSuspense";
import BreedListingCard, {
	type BreedCardProps,
} from "../../../components/SignPostCards/BreedListingCard/BreedListingCard";
import { Button, Form } from "../../../components/common";
import { Cross, Filter, Sort } from "../../../components/common/Icons";
import type { SortOption } from "../../../context/DogSearchApi/DogSearchApi.types";
import { useStaticData } from "../../../hooks/useStaticData";
import { Colours } from "../../../utils/colours";
import { mapBreedToBreedListingCard } from "../../../utils/mappers";
import WrappedSection from "../../WrappedSection/WrappedSection";
import { FiltersButton } from "../../filters/DogListingsFilters/DogListingsFilters";
import * as styles from "./SectionBreedSearchFilteredList.module.scss";

// TODO - TEST STATIC
export const SectionBreedSearchFilteredList: React.FC = () => {
	const { dogBreeds, dogSheddingNames, dogSizeNames, dogActivityLevels } =
		useStaticData([
			"dogBreeds",
			"dogSheddingNames",
			"dogSizeNames",
			"dogActivityLevels",
		]);
	const breedProps: BreedCardProps[] = useMemo(
		() => dogBreeds.map(mapBreedToBreedListingCard),
		[dogBreeds],
	);

	const [selectedBreed, setSelectedBreed] = useState("Any");
	const [selectedSize, setSelectedSize] = useState("Any");
	const [selectedShedding, setSelectedShedding] = useState("Any");
	const [selectedActivityLevel, setSelectedActivityLevel] = useState("Any");
	const [filteredBreeds, setFilteredBreeds] =
		useState<BreedCardProps[]>(breedProps);
	const [filterCount, setFilterCount] = useState(null);
	const [resultCount, setResultCount] = useState<number>(0);

	const [showFilters, setShowFilters] = useState(false);
	const [showSortOptions, setShowSortOptions] = useState(false);
	const [sortBy, setSortBy] = useState<SortOption>("A");

	useEffect(() => {
		setFilteredBreeds(breedProps);
	}, [breedProps]);

	useEffect(() => {
		// Parse query vars
		const queryVars = qs.parse(window.location.search.split("?")[1]) as Record<
			string,
			string
		>;

		let count = 0;

		if (queryVars?.breed && queryVars.breed !== "Any") {
			setSelectedBreed(queryVars.breed);
			count++;
		}
		if (queryVars?.size && queryVars.size !== "Any") {
			setSelectedSize(queryVars.size);
			count++;
		}
		if (queryVars?.shedding && queryVars.shedding !== "Any") {
			setSelectedShedding(queryVars.shedding);
			count++;
		}
		if (queryVars?.activityLevel && queryVars.activityLevel !== "Any") {
			setSelectedActivityLevel(queryVars.activityLevel);
			count++;
		}

		setFilterCount(count);

		filterBreeds(queryVars);
	}, []);

	const filterBreeds = (queryVars: Record<string, string>) => {
		const updatedBreeds = breedProps.filter((breed) => {
			const isBreedMatch =
				queryVars.breed === undefined ||
				queryVars.breed === "Any" ||
				breed.name === queryVars.breed;
			const isSizeMatch =
				queryVars.size === undefined ||
				queryVars.size === "Any" ||
				breed.size === queryVars.size;
			const isSheddingMatch =
				queryVars.shedding === undefined ||
				queryVars.shedding === "Any" ||
				breed.shedding === queryVars.shedding;
			const isActivityLevelMatch =
				queryVars.activityLevel === undefined ||
				queryVars.activityLevel === "Any" ||
				breed.activityLevel === queryVars.activityLevel;

			return (
				isBreedMatch && isSizeMatch && isSheddingMatch && isActivityLevelMatch
			);
		});

		setFilteredBreeds(updatedBreeds);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: adding breedProps creates a loop.
	useEffect(() => {
		const updatedBreeds = breedProps.filter((breed) => {
			const isBreedMatch =
				selectedBreed === "Any" || breed.name === selectedBreed;
			const isSizeMatch = selectedSize === "Any" || breed.size === selectedSize;
			const isSheddingMatch =
				selectedShedding === "Any" || breed.shedding === selectedShedding;
			const isActivityLevelMatch =
				selectedActivityLevel === "Any" ||
				breed.activityLevel === selectedActivityLevel;

			return (
				isBreedMatch && isSizeMatch && isSheddingMatch && isActivityLevelMatch
			);
		});

		setResultCount(updatedBreeds.length);
		setFilterCount(
			[
				selectedBreed,
				selectedSize,
				selectedShedding,
				selectedActivityLevel,
			].filter((filter) => filter !== "Any").length,
		);

		setFilteredBreeds(updatedBreeds);
	}, [selectedBreed, selectedShedding, selectedSize, selectedActivityLevel]);

	useEffect(() => {
		if (showFilters) {
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		} else {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		}
		return () => {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		};
	}, [showFilters]);

	const getSortName = (sortBy: SortOption) => {
		switch (sortBy) {
			case "A":
				return "Alphabetical (A - Z)";
			case "Z":
				return "Alphabetical (Z - A)";
		}
	};

	const sortOptions = [
		{
			title: "Alphabetical (A - Z)",
			onClick: () => setSortBy("A"),
		},
		{
			title: "Alphabetical (Z - A)",
			onClick: () => setSortBy("Z"),
		},
	];

	const sortBreeds = () => {
		switch (sortBy) {
			case "A":
				return (a: BreedCardProps, b: BreedCardProps) => {
					if (a.name < b.name) return -1;
					if (a.name > b.name) return 1;
					return 0;
				};
			case "Z":
				return (a: BreedCardProps, b: BreedCardProps) => {
					if (a.name < b.name) return 1;
					if (a.name > b.name) return -1;
					return 0;
				};
		}
	};

	const clearAllFilters = () => {
		setSelectedBreed("Any");
		setSelectedSize("Any");
		setSelectedShedding("Any");
		setSelectedActivityLevel("Any");
		setFilterCount(0);
		setResultCount(0);
		setFilteredBreeds(breedProps);
	};

	const closeModal = () => {
		setShowFilters(false);
	};

	return (
		<>
			<WrappedSection
				sectionName="SectionBreedSearchFilteredList"
				sectionColour={Colours.SANDY_FUR}
			>
				<h2 data-variation="hideFromJumpLinks">Dog breed search</h2>
				<div className={styles.filters}>
					<FiltersButton
						IconLeft={Filter}
						className={styles.allfilters}
						title={"Filters"}
						onClick={() => {
							setShowFilters(!showFilters);
							setShowSortOptions(false);
						}}
						count={filterCount}
					/>
					<FiltersButton
						IconLeft={Sort}
						className={styles.sortfilters}
						title={"Sort"}
						value={getSortName(sortBy)}
						onClick={() => {
							setShowFilters(false);
							setShowSortOptions((show) => !show);
						}}
						options={showSortOptions && sortOptions}
					/>
				</div>

				<div className={styles.results}>
					{filteredBreeds.sort(sortBreeds()).map((card) => (
						<BreedListingCard key={card.name} {...card} />
					))}
				</div>
			</WrappedSection>
			<SSRSuspense>
				<Modal
					isOpen={showFilters}
					preventScroll
					onRequestClose={closeModal}
					shouldCloseOnEsc
					shouldCloseOnOverlayClick
					shouldFocusAfterRender
					shouldReturnFocusAfterClose
					style={{
						overlay: {
							position: "fixed",
							display: "flex",
							justifyContent: "flex-end",
							height: "100%",
							width: "100%",
							padding: 0,
							margin: 0,
							zIndex: 10001,
							boxSizing: "border-box",
							backgroundColor: "rgba(0, 0, 0, 0.5)",
						},
						content: {
							position: "relative",
							height: "100%",
							top: 0,
							maxWidth: 700,
							zIndex: 10002,
							padding: 0,
							margin: 0,
							right: 0,
							inset: 0,
						},
					}}
				>
					<div className={styles.component}>
						<div className={styles.header}>
							<button
								type="button"
								className={styles.headersection}
								style={{ cursor: "pointer" }}
								onClick={clearAllFilters}
								onKeyDown={(e) => e.key === "Enter" && clearAllFilters}
							>
								Reset
							</button>
							<h2
								data-variation="hideFromJumpLinks"
								className={styles.headersection}
							>
								Filters
							</h2>
							<div className={styles.headersection}>
								<button
									type="button"
									className={styles.closefilters}
									onClick={closeModal}
									onKeyDown={(e) => e.key === "Enter" && closeModal}
								>
									<Cross />
								</button>
							</div>
						</div>
						<div className={styles.filteroptionswrapper}>
							<Form onSubmitFn={() => {}} validationSchema={{}}>
								<div className={styles.filteroptions}>
									<fieldset className={styles.section}>
										<legend className={styles.sectiontitle}>Size</legend>
										<div className={styles.options}>
											{dogSizeNames.map((size) => (
												<FilterOption
													key={size}
													option={size}
													className={styles.sizeoption}
													ariaPressed={selectedSize === size}
													variant={
														selectedSize === size ? "filterselected" : "filter"
													}
													disabled={
														(selectedSize !== size && selectedSize !== "Any") ||
														selectedBreed !== "Any"
													}
													onClick={() =>
														setSelectedSize((prev) =>
															prev === size ? "Any" : size,
														)
													}
												/>
											))}
										</div>
									</fieldset>
									<fieldset className={styles.section}>
										<legend className={styles.sectiontitle}>Shedding</legend>
										<div className={styles.options}>
											{dogSheddingNames.map((shedding) => (
												<FilterOption
													key={shedding}
													option={shedding}
													className={styles.sheddingoption}
													ariaPressed={selectedShedding === shedding}
													variant={
														selectedShedding === shedding
															? "filterselected"
															: "filter"
													}
													disabled={
														(selectedShedding !== shedding &&
															selectedShedding !== "Any") ||
														selectedBreed !== "Any"
													}
													onClick={() =>
														setSelectedShedding((prev) =>
															prev === shedding ? "Any" : shedding,
														)
													}
												/>
											))}
										</div>
									</fieldset>
									<fieldset className={styles.section}>
										<legend className={styles.sectiontitle}>
											Activity level
										</legend>
										<div className={styles.options}>
											{dogActivityLevels.map((activityLevel) => (
												<FilterOption
													key={activityLevel}
													option={activityLevel}
													className={styles.activityoption}
													ariaPressed={selectedActivityLevel === activityLevel}
													variant={
														selectedActivityLevel === activityLevel
															? "filterselected"
															: "filter"
													}
													disabled={
														(selectedActivityLevel !== activityLevel &&
															selectedActivityLevel !== "Any") ||
														selectedBreed !== "Any"
													}
													onClick={() =>
														setSelectedActivityLevel((prev) =>
															prev === activityLevel ? "Any" : activityLevel,
														)
													}
												/>
											))}
										</div>
									</fieldset>
								</div>
							</Form>
						</div>
						<div className={styles.showresults}>
							<div className={styles.buttonwrapper}>
								<Button fullWidth={true} onClick={() => setShowFilters(false)}>
									{`Show (${resultCount}) breeds`}
								</Button>
							</div>
						</div>
					</div>
				</Modal>
			</SSRSuspense>
		</>
	);
};

export default SectionBreedSearchFilteredList;
